import { ServiceBookingRole } from "~/service/models/ServiceBookingRole";
import type { CompanyPageServiceGroup } from "./CompanyPageServiceGroup";
import type { CompanyPageService } from "./findCompanyPageServices";
import isDeleted from "~/kernel/softDelete/isDeleted";
import groupServices from "~/service/helpers/groupServices";
import type { Service } from "~/service/models/Service";
import type { ServiceGroup } from "@prisma/client";
import type { FakeLocalServiceGroup } from "~/service/components/SortServicesFormFields/LocalServiceGroup";

export function buildServicesViewObject({
  services,
  serviceGroups,
}: {
  services: CompanyPageService[];
  serviceGroups: CompanyPageServiceGroup[];
}): ServicesViewObject {
  const mainServices = services.filter(isMainServiceVisible).map((service) => {
    return {
      ...service,
      addOnServices: service.addOnAssignments
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((ass) => ass.addOnService)
        .filter((ads) => isAddOnServiceVisible(ads)),
    };
  });

  if (mainServices.length < 1) {
    return {
      display: "NO_SERVICES" as const,
    };
  }

  if (serviceGroups.length > 0) {
    const groupedServices = groupServices(mainServices, serviceGroups);
    return {
      display: "SERVICE_GROUPS" as const,
      serviceGroups: groupedServices,
    };
  }

  return {
    display: "SERVICES" as const,
    services: mainServices,
  };
}

function isServiceVisible(service: Service) {
  return !isDeleted(service) && service.isVisibleForCustomer;
}

function isMainServiceVisible(service: CompanyPageService) {
  return (
    service.bookingRole === ServiceBookingRole.Main && isServiceVisible(service)
  );
}

function isAddOnServiceVisible(service: Service) {
  return (
    service.bookingRole === ServiceBookingRole.AddOn &&
    isServiceVisible(service)
  );
}

export type ServiceGroupViewObject = (ServiceGroup | FakeLocalServiceGroup) & {
  services: MainServiceViewObject[];
};

export type ServicesViewObject =
  | {
      display: "NO_SERVICES";
    }
  | {
      display: "SERVICE_GROUPS";
      serviceGroups: ServiceGroupViewObject[];
    }
  | {
      display: "SERVICES";
      services: MainServiceViewObject[];
    };

export type MainServiceViewObject = Service & {
  addOnServices: Service[];
};
